import React, { FunctionComponent, KeyboardEvent, useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import "./ChatHistoryTable.scss";
import { LoadingIndicator } from "../../common/LoadingIndicator";
import { NoResultsFound } from "../../common/NoResultsFound";
import {
    HeaderField,
    Table,
    TableBody,
    TableField,
    TableHeader,
    TableRow,
    UpIcon
} from "@panviva/panviva-react-ui";
import {
    SK_CHAT_HISTORY_TABLE_CLASS,
    TABLE_COLUMN_CLASSES,
    TABLE_COLUMN_HEADERS,
    ROWS_PER_PAGE_OPTIONS,
    INITIAL_LOAD_FAILURE_MESSAGE,
    INITIAL_LOAD_FAILURE_DESCRIPTION,
    UNAUTHORIZED_REDIRECT_MESSAGE,
    UNAUTHORIZED_REDIRECT_DESCRIPTION,
    NO_CHAT_SESSION_FOUND,
    NO_RESULTS_MATCH_FILTERS,
    ADJUST_FILTERS_MESSAGE,
    SESSIONS_APPEAR_MESSAGE
} from "../../../constants/sidekick";
import { ChatHistoryTableProps } from "../../../types/sidekick";
import { formatTimestamp } from "../../../utils/transformers/sidekickTransformers";
import {
    calculateColumnWidths,
    getColumnDisplayName,
    SortField
} from "../../../utils/tableUtils";

/**
 * Delays (in milliseconds) for scroll position retry attempts.
 * Multiple attempts are necessary because the Panviva Table component
 * restores scroll position after initial render, so we need to force
 * the scroll position multiple times to ensure it sticks.
 */
const SCROLL_RETRY_DELAYS = [0, 50, 150, 300, 500];

export const ChatHistoryTable: FunctionComponent<ChatHistoryTableProps & { isUnauthorizedInitialLoadFailure?: boolean }> = ({
    currentItems,
    currentPage,
    rowsPerPage,
    totalItems,
    totalPages,
    handleFirstPage,
    handlePrevPage,
    handleNextPage,
    handleLastPage,
    handleChangeRowsPerPage,
    isLoading,
    sortField,
    sortDirection,
    handleSort,
    scrollKey,
    hasActiveFilters,
    disableSorting = false,
    isInitialLoadFailure = false,
    isUnauthorizedInitialLoadFailure = false
}) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const errorMessageRef = useRef<HTMLDivElement>(null);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const resizeTimeoutRef = useRef<number | null>(null);
    const [resultsAnnouncement, setResultsAnnouncement] = useState<string>('');
    const previousScrollKeyRef = useRef<number | undefined>(scrollKey);

    // Memoized function to calculate column widths
    const calculateAndSetColumnWidths = useCallback(() => {
        if (!tableContainerRef.current) {
            return;
        }

        const containerWidth = tableContainerRef.current.clientWidth || 0;
        const calculatedWidths = calculateColumnWidths(currentItems, containerWidth);
        setColumnWidths(calculatedWidths);
    }, [currentItems]);

    // Calculate column widths when data or page size changes
    useEffect(() => {
        calculateAndSetColumnWidths();
    }, [currentItems, rowsPerPage, calculateAndSetColumnWidths]);

    // Focus management for initial load failure errors
    useEffect(() => {
        if (isInitialLoadFailure && !isLoading && currentItems.length === 0) {
            // Move focus to error message for accessibility
            // Use requestAnimationFrame for more reliable timing across different devices
            let timerId: number;
            requestAnimationFrame(() => {
                timerId = window.setTimeout(() => {
                    errorMessageRef.current?.focus();
                }, 150);
            });
            return () => {
                if (timerId) {
                    clearTimeout(timerId);
                }
            };
        }
    }, [isInitialLoadFailure, isLoading, currentItems.length]);

    // Announce results for screen readers when filters/sorts are applied (scrollKey changes)
    useEffect(() => {
        if (isLoading) return;

        // scrollKey changes when filter/sort is applied, not on pagination or rows-per-page change
        const hasScrollKeyChanged = scrollKey !== undefined && scrollKey !== previousScrollKeyRef.current;

        if (hasScrollKeyChanged) {
            const currentLength = currentItems.length;

            if (currentLength === 0 && hasActiveFilters) {
                setResultsAnnouncement(`${NO_RESULTS_MATCH_FILTERS}. ${ADJUST_FILTERS_MESSAGE}`);
            } else if (currentLength === 0 && !hasActiveFilters) {
                setResultsAnnouncement(`${NO_CHAT_SESSION_FOUND}. ${SESSIONS_APPEAR_MESSAGE}`);
            } else if (totalItems === 1) {
                setResultsAnnouncement(`Showing 1 result`);
            } else {
                setResultsAnnouncement(`Showing ${totalItems} results`);
            }

            previousScrollKeyRef.current = scrollKey;
        }

        // Clear announcement after 3 seconds (cleanup always runs regardless of condition)
        const timer = setTimeout(() => setResultsAnnouncement(''), 3000);
        return () => clearTimeout(timer);
    }, [scrollKey, currentItems.length, isLoading, hasActiveFilters, totalItems]);

    // Scroll to top whenever scrollKey increments (page, sort, or filter changed).
    // scrollKey is never incremented on rows-per-page change, so scroll is preserved then.
    useEffect(() => {
        if (!scrollKey) return; // skip initial mount (scrollKey starts at 0)

        const timeouts: number[] = [];
        SCROLL_RETRY_DELAYS.forEach((delay) => {
            timeouts.push(window.setTimeout(() => {
                requestAnimationFrame(() => {
                    const table = document.querySelector('.chat-history-table') as HTMLElement;
                    if (table) table.scrollTop = 0;
                    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
                });
            }, delay));
        });
        return () => timeouts.forEach(t => window.clearTimeout(t));
    }, [scrollKey]);

    // Handle window resize with debounce
    useEffect(() => {
        const handleResize = () => {
            // Add resizing class for transition effect
            setIsResizing(true);

            // Clear any existing timeout
            if (resizeTimeoutRef.current) {
                window.clearTimeout(resizeTimeoutRef.current);
            }

            // Set new timeout for debounce
            resizeTimeoutRef.current = window.setTimeout(() => {
                calculateAndSetColumnWidths();
                setIsResizing(false);
                resizeTimeoutRef.current = null;
            }, 200);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeoutRef.current) {
                window.clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [calculateAndSetColumnWidths]);

    // Get column style based on calculated width
    const getColumnStyle = useCallback((field: string): React.CSSProperties => {
        const width = columnWidths[field];
        return width ? { width: `${width}px` } : {};
    }, [columnWidths]);

    // Render sort icon based on current sort state
    const renderSortIcon = useCallback((field: SortField) => {
        if (disableSorting) {
            return null;
        }

        const columnName = getColumnDisplayName(field, TABLE_COLUMN_HEADERS);
        const isActiveSort = sortField === field;

        if (!isActiveSort) {
            return (
                <span className="sort-icon sort-icon-default" aria-hidden="true">
                    <UpIcon icon="sort-unsorted" color="#6B7786" />
                </span>
            );
        }

        const isAscending = sortDirection === 'asc';
        return (
            <span
                className={`sort-icon sort-icon-${isAscending ? 'asc' : 'desc'}`}
                aria-label={`Sorted by ${columnName} in ${isAscending ? 'ascending' : 'descending'} order`}
                role="img"
            >
                <UpIcon
                    icon={isAscending ? 'sort-ascending' : 'sort-descending'}
                    color="#0C5CC5"
                />
            </span>
        );
    }, [disableSorting, sortField, sortDirection]);

    // Navigation handler with text selection prevention
    const dispatch = useDispatch();
    const onRowClick = useCallback((conversationId: string, conversationSubject: string, event: React.MouseEvent) => {
        // Check if any text is selected
        const selectedText = window.getSelection()?.toString().trim() || '';

        // Only navigate if no text is selected
        if (!selectedText) {
            // Store the conversation subject in Redux state before navigating
            dispatch({
                type: 'sidekick/setConversationSubject',
                payload: { conversationId, conversationSubject }
            });

            // Also store in sessionStorage for browser navigation persistence
            if (conversationId) {
                sessionStorage.setItem(`chat_subject_${conversationId}`, conversationSubject);
            }

            window.open(`/sidekick/chat-history/${conversationId}`, '_blank');
        } else {
            // Prevent default to keep text selection
            event.preventDefault();
        }
    }, [dispatch]);

    // Keyboard accessibility handlers
    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>, conversationId: string, conversationSubject: string) => {
        // Handle arrow key navigation between rows
        const currentRow = event.currentTarget;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextRow = currentRow.nextElementSibling as HTMLElement | null;
            nextRow?.focus();
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevRow = currentRow.previousElementSibling as HTMLElement | null;
            prevRow?.focus();
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            // Store in sessionStorage before navigation (in addition to what onRowClick does)
            if (conversationId) {
                sessionStorage.setItem(`chat_subject_${conversationId}`, conversationSubject);
            }
            onRowClick(conversationId, conversationSubject, event as unknown as React.MouseEvent);
        }
    }, [onRowClick]);

    const handleHeaderKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>, field: SortField) => {
        if (disableSorting) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSort(field);
        }
    }, [disableSorting, handleSort]);

    return (
        <div
            className={`${SK_CHAT_HISTORY_TABLE_CLASS} ${isResizing ? 'resizing' : ''}`}
            ref={tableContainerRef}
        >
            {/* Use inline loading indicator with targetElementId to avoid duplicate global indicators */}
            <LoadingIndicator busy={isLoading} />
            <Table
                className={`chat-history-table dynamic-columns table-with-sticky-pagination ${!isLoading && currentItems.length === 0 ? 'empty-state' : ''}`}
                aria-label="Chat history data"
                totalCount={totalItems}
                totalPages={totalPages}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
                setRowsPerPage={handleChangeRowsPerPage}
                handleFirstPage={handleFirstPage}
                handlePrevPage={handlePrevPage}
                handleNextPage={handleNextPage}
                handleLastPage={handleLastPage}
                style={{ position: 'relative' }}
            >
                <TableHeader>
                    <TableRow type="header" role="row">
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.EMAIL_ID}
                            onClick={() => !disableSorting && handleSort('emailId')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'emailId')}
                            aria-sort={sortField === 'emailId' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Email ID"
                            role="columnheader"
                            style={getColumnStyle('emailId')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.EMAIL_ID}{!disableSorting && <> {renderSortIcon('emailId')}</>}
                        </HeaderField>
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.CONVERSATION_SUBJECT}
                            onClick={() => !disableSorting && handleSort('conversationSubject')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'conversationSubject')}
                            aria-sort={sortField === 'conversationSubject' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Conversation Subject"
                            role="columnheader"
                            style={getColumnStyle('conversationSubject')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.CONVERSATION_SUBJECT}{!disableSorting && <> {renderSortIcon('conversationSubject')}</>}
                        </HeaderField>
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.CONVERSATION_CREATED_AT}
                            onClick={() => !disableSorting && handleSort('conversationCreatedAt')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'conversationCreatedAt')}
                            aria-sort={sortField === 'conversationCreatedAt' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Conversation Created At"
                            role="columnheader"
                            style={getColumnStyle('conversationCreatedAt')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.CONVERSATION_CREATED_AT}{!disableSorting && <> {renderSortIcon('conversationCreatedAt')}</>}
                        </HeaderField>
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.CONVERSATION_FEEDBACK}
                            onClick={() => !disableSorting && handleSort('conversationFeedback')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'conversationFeedback')}
                            aria-sort={sortField === 'conversationFeedback' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Conversation Feedback"
                            role="columnheader"
                            style={getColumnStyle('conversationFeedback')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.CONVERSATION_FEEDBACK}{!disableSorting && <> {renderSortIcon('conversationFeedback')}</>}
                        </HeaderField>
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.FEEDBACK_REASON}
                            onClick={() => !disableSorting && handleSort('feedbackReason')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'feedbackReason')}
                            aria-sort={sortField === 'feedbackReason' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Feedback Reason"
                            role="columnheader"
                            style={getColumnStyle('feedbackReason')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.FEEDBACK_REASON}{!disableSorting && <> {renderSortIcon('feedbackReason')}</>}
                        </HeaderField>
                        <HeaderField
                            className={TABLE_COLUMN_CLASSES.CURATED_RESPONSES}
                            onClick={() => !disableSorting && handleSort('curatedResponses')}
                            onKeyDown={(e) => handleHeaderKeyDown(e, 'curatedResponses')}
                            aria-sort={sortField === 'curatedResponses' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                            aria-label="Curated Response"
                            role="columnheader"
                            style={getColumnStyle('curatedResponses')}
                            tabIndex={disableSorting ? -1 : 0}
                        >
                            {TABLE_COLUMN_HEADERS.CURATED_RESPONSES}{!disableSorting && <> {renderSortIcon('curatedResponses')}</>}
                        </HeaderField>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {(!isLoading && currentItems.length === 0) ? (
                        <TableRow className="empty-state-row" role="row">
                            <TableField colSpan={6} style={{ textAlign: 'center', padding: 0, border: 'none' }} role="cell">
                                <NoResultsFound
                                    ref={isInitialLoadFailure ? errorMessageRef : null}
                                    message={
                                        isUnauthorizedInitialLoadFailure
                                            ? UNAUTHORIZED_REDIRECT_MESSAGE
                                            : (isInitialLoadFailure
                                                ? INITIAL_LOAD_FAILURE_MESSAGE
                                                : (hasActiveFilters ? NO_RESULTS_MATCH_FILTERS : NO_CHAT_SESSION_FOUND))
                                    }
                                    description={
                                        isUnauthorizedInitialLoadFailure
                                            ? UNAUTHORIZED_REDIRECT_DESCRIPTION
                                            : (isInitialLoadFailure
                                                ? INITIAL_LOAD_FAILURE_DESCRIPTION
                                                : (hasActiveFilters ? ADJUST_FILTERS_MESSAGE : SESSIONS_APPEAR_MESSAGE))
                                    }
                                />
                            </TableField>
                        </TableRow>
                    ) : (
                        currentItems.map((item) => (
                            <TableRow
                                key={item.conversationId}
                                role="row"
                                onClick={(e) => onRowClick(item.conversationId, item.conversationSubject, e)}
                                onKeyDown={(e) => handleKeyDown(e, item.conversationId, item.conversationSubject)}
                                style={{ cursor: "pointer" }}
                                tabIndex={0}
                                aria-label={`Email ID: ${item.emailId}, Conversation Subject: ${item.conversationSubject}, Conversation Created At: ${formatTimestamp(item.conversationCreatedAt)}, Conversation Feedback: ${item.conversationFeedback}, Feedback Reason: ${item.feedbackReason}, Curated Response: ${item.curatedResponses}`}
                            >
                                <TableField className={TABLE_COLUMN_CLASSES.EMAIL_ID} role="cell" style={getColumnStyle('emailId')}>{item.emailId}</TableField>
                                <TableField className={TABLE_COLUMN_CLASSES.CONVERSATION_SUBJECT} role="cell" style={getColumnStyle('conversationSubject')}>{item.conversationSubject}</TableField>
                                <TableField className={TABLE_COLUMN_CLASSES.CONVERSATION_CREATED_AT} role="cell" style={getColumnStyle('conversationCreatedAt')}>{formatTimestamp(item.conversationCreatedAt)}</TableField>
                                <TableField className={TABLE_COLUMN_CLASSES.CONVERSATION_FEEDBACK} role="cell" style={getColumnStyle('conversationFeedback')}>{item.conversationFeedback}</TableField>
                                <TableField className={TABLE_COLUMN_CLASSES.FEEDBACK_REASON} role="cell" style={getColumnStyle('feedbackReason')}>{item.feedbackReason}</TableField>
                                <TableField className={TABLE_COLUMN_CLASSES.CURATED_RESPONSES} role="cell" style={getColumnStyle('curatedResponses')}>{item.curatedResponses}</TableField>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            {/* Screen reader announcement for results */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'fixed',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            >
                {resultsAnnouncement}
            </div>
        </div>
    );
};